import React, { useState, useCallback, useEffect } from 'react';
import { Text, Stack, Group, Button, Container, Title, Alert } from '@mantine/core';
import { Dropzone, FileWithPath } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import { Link } from 'react-router-dom';
import type { AxiosProgressEvent } from 'axios';
import Layout from '../components/Layout';
import RestaurantSelector from '../components/upload/RestaurantSelector';
import ProgressDisplay from '../components/upload/ProgressDisplay';
import StatusBadge from '../components/upload/StatusBadge';
import { useActiveUploadSession } from '../context/ActiveUploadSessionContext';
import { prepareUploadFormData, validateUploadForm, UploadFormFields, RestaurantOption } from '../utils/uploadFormData';
import api from '../services/api';

/**
 * Upload Menu by Photo Page
 * Uploads menu photos with uploadMode='v2' via the normal upload flow (POST /upload).
 * v2 mode generates translations, background context, and extracts ingredients into the normalized Ingredient table.
 * SSE is owned by ActiveUploadSessionProvider so it stays open when navigating to the menu preview.
 */
const FastUpload: React.FC = () => {
  const [files, setFiles] = useState<FileWithPath[]>([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState<string | null>(null);
  const [newRestaurantName, setNewRestaurantName] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [description, setDescription] = useState('');
  const [sourceLanguage, setSourceLanguage] = useState<string>('jp');
  const [loading, setLoading] = useState(false);
  const [restaurants, setRestaurants] = useState<RestaurantOption[]>([]);
  const [requestUploadProgress, setRequestUploadProgress] = useState<number | null>(null);

  const uploadMode = 'v2';

  const {
    startSession,
    clearSession,
    uploadStatus,
    uploadProgress,
    currentFileStatus,
    currentFileIndex,
    detailedStatus,
    currentStage,
    processedFiles,
    uploadResponse,
    clientId
  } = useActiveUploadSession();

  // Finish loading state when server reports done or failure (SSE owned by provider)
  useEffect(() => {
    if (uploadResponse) {
      setFiles([]);
      setLoading(false);
    }
  }, [uploadResponse]);

  useEffect(() => {
    if (uploadStatus === 'Upload failed') {
      setLoading(false);
    }
  }, [uploadStatus]);

  const handleFileDrop = (droppedFiles: FileWithPath[]) => {
    setFiles(prev => [...prev, ...droppedFiles]);
  };

  const removeFile = (indexToRemove: number) => {
    setFiles(files.filter((_, index) => index !== indexToRemove));
  };

  // Handle form submission — posts to /upload with uploadMode='v2'
  const handleUpload = useCallback(async () => {
    const formFields: UploadFormFields = {
      newRestaurantName,
      adminNotes,
      description,
      uploadMode,
      sourceLanguage
    };

    const validation = validateUploadForm(selectedRestaurant, files, formFields);
    if (!validation.isValid) {
      notifications.show({
        title: 'Error',
        message: validation.error,
        color: 'red',
      });
      return;
    }

    setLoading(true);
    clearSession();
    setRequestUploadProgress(0);

    try {
      const formData = prepareUploadFormData(
        files,
        'menuImages',
        selectedRestaurant,
        restaurants,
        formFields
      );

      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (event: AxiosProgressEvent) => {
          if (!event.total) {
            setRequestUploadProgress(0);
            return;
          }
          const percent = Math.round((event.loaded / event.total) * 100);
          setRequestUploadProgress(Math.max(0, Math.min(100, percent)));
        }
      });

      setRequestUploadProgress(100);
      if (response.data?.clientId) {
        startSession(response.data.clientId);
      }
    } catch (error: any) {
      console.error('Error uploading menu:', error);
      setLoading(false);
      setRequestUploadProgress(null);
      notifications.show({
        title: 'Error',
        message: error.response?.data?.message || 'Failed to upload menu',
        color: 'red',
      });
    }
  }, [selectedRestaurant, files, newRestaurantName, adminNotes, description, sourceLanguage, restaurants, clearSession, startSession]);

  // Get status badge for processed files
  const getStatusBadge = (status: string) => {
    return <StatusBadge status={status} />;
  };

  const showProgress = loading || !!clientId;

  return (
    <Layout>
      <Container>
        <Title order={1} mb="md">
          Upload Menu by Photo
        </Title>

        {/* Photo Dropzone */}
        <Dropzone
          onDrop={handleFileDrop}
          accept={{
            'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
            'application/pdf': ['.pdf'],
          }}
          mb="md"
        >
          <Text ta="center" size="lg">
            Drag and drop menu files here, or click to select files
          </Text>
          <Text ta="center" size="sm" c="dimmed" mt={7}>
            Attach as many files as you like, each file should not exceed 5mb
          </Text>
        </Dropzone>

        {files.length > 0 && (
          <Stack mb="md">
            <Text fw={500}>Files to upload:</Text>
            {files.map((file, index) => (
              <Group key={index} justify="space-between" wrap="nowrap">
                <Text size="sm" style={{ wordBreak: 'break-all' }}>
                  {file.name}
                </Text>
                <Button
                  variant="subtle"
                  color="red"
                  size="xs"
                  onClick={() => removeFile(index)}
                >
                  Remove
                </Button>
              </Group>
            ))}
          </Stack>
        )}

        {/* Restaurant Selection */}
        <RestaurantSelector
          selectedRestaurant={selectedRestaurant}
          onRestaurantChange={setSelectedRestaurant}
          newRestaurantName={newRestaurantName}
          onNewRestaurantNameChange={setNewRestaurantName}
          adminNotes={adminNotes}
          onAdminNotesChange={setAdminNotes}
          description={description}
          onDescriptionChange={setDescription}
          loading={loading}
          onRestaurantsChange={setRestaurants}
        />

        {/* Source Language Selection (simplified - no upload mode selector) */}
        <Stack gap="md" mb="md">
          <Text size="sm" fw={500}>Source Language</Text>
          <Text size="xs" c="dimmed">
            The original language of the menu items. Defaults to Japanese.
          </Text>
          <select
            value={sourceLanguage}
            onChange={(e) => setSourceLanguage(e.target.value)}
            disabled={loading}
            style={{
              padding: '8px',
              borderRadius: '4px',
              border: '1px solid #ced4da',
              fontSize: '14px'
            }}
          >
            <option value="jp">Japanese</option>
            <option value="ko">Korean</option>
            <option value="zh">Chinese</option>
            <option value="en">English</option>
          </select>
        </Stack>

        {/* Progress Display — stays visible while SSE session runs (including after redirect to menu) */}
        <ProgressDisplay
          loading={showProgress}
          uploadStatus={uploadStatus}
          uploadProgress={uploadProgress}
          currentFileStatus={currentFileStatus}
          currentFileIndex={currentFileIndex}
          totalFiles={files.length}
          currentStage={currentStage}
          detailedStatus={detailedStatus}
          requestUploadProgress={requestUploadProgress}
        />

        {/* Upload Complete Display — if user is still on this page when processing finishes */}
        {uploadResponse && (
          <Stack mb="md">
            <Alert title="Upload Complete" color="green">
              <Stack gap="xs">
                <Text>
                  <strong>{uploadResponse.restaurantName}</strong> - {uploadResponse.menuName}
                </Text>
                <Text size="sm">
                  Successfully processed {uploadResponse.filesProcessed || uploadResponse.itemsProcessed} file(s)
                </Text>
                <Text mt="xs">
                  <Link to={`/menus/${uploadResponse.menuId}`}>
                    View the uploaded menu
                  </Link>
                </Text>
              </Stack>
            </Alert>

            {/* Processed Files Table */}
            {processedFiles.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #dee2e6' }}>File</th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #dee2e6' }}>Status</th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #dee2e6' }}>Items Extracted</th>
                  </tr>
                </thead>
                <tbody>
                  {processedFiles.map((file, index) => (
                    <tr key={index}>
                      <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6' }}>{file.originalName}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6' }}>{getStatusBadge(file.status)}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6' }}>
                        {file.status === 'complete' ? (
                          <StatusBadge 
                            status="complete" 
                            color="green"
                          >
                            {file.itemsExtracted || file.itemsProcessed} items
                          </StatusBadge>
                        ) : file.status === 'error' ? (
                          <Text size="sm" c="red">{file.error}</Text>
                        ) : (
                          <Text size="sm" c="dimmed">-</Text>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Stack>
        )}

        {/* Upload Button */}
        <Button
          onClick={handleUpload}
          loading={loading}
          disabled={files.length === 0 || !selectedRestaurant || loading}
          fullWidth
        >
          {loading ? 'Uploading...' : 'Upload Files'}
        </Button>
      </Container>
    </Layout>
  );
};

export default FastUpload;
